import { EditOutlined } from '@ant-design/icons';
import {
  Button, Drawer, Flex, Input, Row, Typography,
} from 'antd';
import { addAddressToCustomer } from 'api/customers';
import Map from 'components/Map';
import useConfig from 'hooks/useConfig';
import React, { useEffect, useState } from 'react';
import { captureException } from 'utils/errors';
import { App } from 'antd';

const STEPS = {
  GET_LOCATION: 'GET_LOCATION',
  GET_INFO: 'GET_INFO',
};

function AddAddressDrawer({ customerId, open, onClose, onSuccess }) {
  const { defaultLat, defaultLong } = useConfig('general') || {};
  const [step, setStep] = useState(STEPS.GET_LOCATION);
  const [address, setAddress] = useState({ label: null, details: null, deliveryInstructions: null });
  const [saveLoading, setSaveLoading] = useState(false);
  const [location, setLocation] = useState({
    lat: defaultLat || 40.7128,
    lng: defaultLong || -74.0060,
    isDefault: true,
  });
  const isFirstSearch = React.useRef(true);
  const { message } = App.useApp();

  useEffect(() => {
    if (open) {
      setStep(STEPS.GET_LOCATION);
      setAddress({ label: null, details: null, deliveryInstructions: null });
      setLocation({
        lat: defaultLat || 40.7128,
        lng: defaultLong || -74.0060,
        isDefault: true,
      });
      isFirstSearch.current = true;
    }
  }, [open, defaultLat, defaultLong]);

  const handleClose = () => {
    setStep(STEPS.GET_LOCATION);
    setAddress({ label: null, details: null, deliveryInstructions: null });
    onClose();
  };

  const confirmLocation = () => {
    if (isFirstSearch.current) {
      isFirstSearch.current = false;
    }
    setStep(STEPS.GET_INFO);
  };

  const handleSave = () => {
    const data = {
      title: address.label,
      address: location?.text,
      latitude: location?.lat,
      longitude: location?.lng,
      city: location?.city || '',
      state: location?.state || '',
      country: location?.country || '',
      country_code: location?.country_code || '',
      zip_code: location?.zip_code || '',
      details: address.details,
      delivery_instructions: address.deliveryInstructions,
      customer: customerId,
    };

    setSaveLoading(true);
    addAddressToCustomer(data)
      .then((res) => {
        const newAddress = res?.data ?? res;
        onSuccess?.(newAddress);
        message.success('Address added successfully');
        handleClose();
      })
      .catch((e) => {
        captureException(e);
        message.error(e?.response?.data?.detail ?? e?.response?.data?.title ?? 'Failed to add address');
      })
      .finally(() => setSaveLoading(false));
  };

  const handleUpdateAddressDetail = (payload) => {
    setAddress((adr) => ({ ...adr, ...payload }));
  };

  const canConfirmLocation = location?.lat && location?.lng && location?.text;
  const canSave = canConfirmLocation && step === STEPS.GET_INFO;

  return (
    <Drawer
      title={step === STEPS.GET_LOCATION ? 'Choose Location' : 'Add Address'}
      width={572}
      open={open}
      onClose={handleClose}
      footer={(
        <Flex justify="flex-end" gap={16}>
          <Button onClick={handleClose}>
            Cancel
          </Button>
          {step === STEPS.GET_LOCATION ? (
            <Button
              type="primary"
              onClick={confirmLocation}
              disabled={!canConfirmLocation}
            >
              Confirm location
            </Button>
          ) : (
            <Button
              type="primary"
              onClick={handleSave}
              loading={saveLoading}
              disabled={!canSave}
            >
              Add address
            </Button>
          )}
        </Flex>
      )}
    >
      {step === STEPS.GET_LOCATION ? (
        <Flex vertical gap={16}>
          <Map
            center={location}
            onChange={(newLoc) => {
              if (newLoc?.lat !== location?.lat || newLoc?.lng !== location?.lng) {
                setLocation(newLoc);
              }
            }}
            height={300}
          >
            <Map.FixedMarker />
            <Map.SearchInput
              hasCurrentLocationSelector={false}
              onChange={(e) => setLocation(e)}
            />
          </Map>
          {location?.text && (
            <Typography.Text type="secondary">{location.text}</Typography.Text>
          )}
        </Flex>
      ) : (
        <Flex vertical gap={16}>
          <div style={{ pointerEvents: 'none' }}>
            <Map
              center={location}
              height={148}
              zoomControl={false}
              fullscreenControl={false}
            >
              <Map.FixedMarker />
            </Map>
          </div>
          <Flex justify="space-between" align="center">
            <Typography.Text type="secondary">{location?.text}</Typography.Text>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => setStep(STEPS.GET_LOCATION)}
            >
              Adjust pin
            </Button>
          </Flex>

          <Row>
            <Typography.Text>Address label</Typography.Text>
            <Input
              className="details-input"
              type="text"
              value={address.label}
              onChange={({ target: { value } }) => handleUpdateAddressDetail({ label: value })}
              placeholder="ex. Home, Office"
              style={{ marginTop: 4 }}
            />
          </Row>
          <Row>
            <Typography.Text>Details</Typography.Text>
            <Input
              className="details-input"
              type="text"
              value={address.details}
              onChange={({ target: { value } }) => handleUpdateAddressDetail({ details: value })}
              placeholder="Apt, floor, suite, etc"
              style={{ marginTop: 4 }}
            />
          </Row>
          <Row>
            <Typography.Text>Delivery instructions</Typography.Text>
            <Input
              className="details-input"
              type="text"
              value={address.deliveryInstructions}
              onChange={({ target: { value } }) => handleUpdateAddressDetail({ deliveryInstructions: value })}
              placeholder="Example: Please knock instead of using the doorbell"
              style={{ marginTop: 4 }}
            />
          </Row>
        </Flex>
      )}
    </Drawer>
  );
}

export default AddAddressDrawer;
